const LEGACY_SHOPIFY_COOKIES = ['_shopify_y', '_shopify_s'];
export const SHOPIFY_CONSENT_READY_EVENT = 'shopify:consent-ready';

function getLegacyShopifyTokens(cookieString) {
  const values = {};
  cookieString.split(';').forEach((part) => {
    const [name, ...value] = part.trim().split('=');
    // Match the previous SDK's duplicate-name behavior during migration.
    if (LEGACY_SHOPIFY_COOKIES.includes(name)) values[name] = value.join('=');
  });
  return { uniqueToken: values._shopify_y, visitToken: values._shopify_s };
}

function hasShopifyTrackingValues(values) {
  return [values?.uniqueToken, values?.visitToken].every(
    (value) =>
      typeof value === 'string' &&
      value.length > 0 &&
      !value.startsWith('00000000-')
  );
}

export function readShopifyConsent() {
  if (typeof window === 'undefined') return null;
  const privacy = window.Shopify?.customerPrivacy;
  if (
    !privacy?.analyticsProcessingAllowed ||
    !privacy?.currentVisitorConsent ||
    !privacy?.marketingAllowed ||
    !privacy?.saleOfDataAllowed
  )
    return null;
  try {
    const consent = privacy.currentVisitorConsent();
    const preference = (value) => {
      if (value === 'yes') return true;
      if (value === 'no') return false;
      return null;
    };
    return {
      analyticsAllowed: privacy.analyticsProcessingAllowed(),
      marketingAllowed: privacy.marketingAllowed(),
      saleOfDataAllowed: privacy.saleOfDataAllowed(),
      visitorConsent: {
        analytics: preference(consent.analytics),
        marketing: preference(consent.marketing),
        preferences: preference(consent.preferences),
        saleOfData: preference(consent.sale_of_data),
      },
    };
  } catch {
    return null;
  }
}

function initializeShopifyConsent(value, requestedConsent) {
  const privacy = window.Shopify?.customerPrivacy;
  if (
    typeof value !== 'string' ||
    !value ||
    !privacy ||
    privacy.cachedConsent ||
    JSON.stringify(readShopifyConsent()) !== JSON.stringify(requestedConsent)
  )
    return;
  // Shopify's encrypted cookies are not readable by the privacy API after a
  // reload. Initialize its cache from Shopify's response, as Hydrogen does,
  // without overwriting a visitor choice made while this request was pending.
  privacy.cachedConsent = value;
  document.dispatchEvent(new Event(SHOPIFY_CONSENT_READY_EVENT));
}

function getLegacyTokens() {
  const original = getLegacyShopifyTokens(document.cookie);
  // The old hook wrote host-only cookies, while checkout writes the same names
  // for the parent domain. Cookie reads cannot reliably identify their scope.
  // If both exist, remove only the obsolete host-only copy before forwarding
  // cookies to Shopify, so checkout's shared identity is unambiguous.
  LEGACY_SHOPIFY_COOKIES.forEach((name) => {
    const duplicates = document.cookie
      .split(';')
      .filter((part) => part.trim().startsWith(`${name}=`));
    if (duplicates.length > 1)
      document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
  });
  const current = getLegacyShopifyTokens(document.cookie);
  return {
    uniqueToken: current.uniqueToken || original.uniqueToken,
    visitToken: current.visitToken || original.visitToken,
  };
}

let trackingRequest;
let trackingConsent;

function postShopifyTracking(body, signal) {
  // Storefront scripts can wrap fetch and leave this handshake pending. Keep
  // the identity/consent request on the browser's working XHR transport.
  if (typeof XMLHttpRequest === 'undefined') {
    return fetch('/api/shopify-tracking', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      signal,
    });
  }
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let finished = false;
    let abort;
    const finish = (callback, value) => {
      if (finished) return;
      finished = true;
      signal.removeEventListener('abort', abort);
      callback(value);
    };
    abort = () => {
      xhr.abort();
      finish(reject, signal.reason || new Error('Shopify tracking aborted'));
    };
    xhr.open('POST', '/api/shopify-tracking');
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.onload = () => {
      if (!xhr.status) {
        finish(reject, new TypeError('Shopify tracking network error'));
        return;
      }
      finish(resolve, {
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        json: async () => JSON.parse(xhr.responseText),
      });
    };
    xhr.onerror = () =>
      finish(reject, new TypeError('Shopify tracking network error'));
    xhr.onabort = () =>
      finish(reject, signal.reason || new Error('Shopify tracking aborted'));
    signal.addEventListener('abort', abort, { once: true });
    try {
      if (signal.aborted) abort();
      else xhr.send(body);
    } catch (error) {
      finish(reject, error);
    }
  });
}

async function requestShopifyTracking(syncConsent) {
  const consent = readShopifyConsent();
  if (!consent || (!consent.analyticsAllowed && !syncConsent)) return null;
  const legacyTokens = consent.analyticsAllowed
    ? await getLegacyTokens()
    : undefined;
  const current = readShopifyConsent();
  if (!current || JSON.stringify(current) !== JSON.stringify(consent))
    return null;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 10000);
  let cookieDomain;
  let values;
  try {
    const response = await postShopifyTracking(
      JSON.stringify({
        visitorConsent: consent.visitorConsent,
        legacyTokens,
        globalPrivacyControl: navigator.globalPrivacyControl === true,
      }),
      controller.signal
    );
    if (!response.ok) {
      const error = new Error('Shopify tracking initialization failed');
      error.retryable = response.status === 429 || response.status >= 500;
      throw error;
    }
    ({ cookieDomain, ...values } = await response.json());
  } finally {
    clearTimeout(timeout);
  }
  initializeShopifyConsent(values.consent, consent);
  // Only remove the app's obsolete host-only copies after the server has set
  // canonical shared cookies successfully. Do not delete on localhost/preview.
  if (cookieDomain) {
    LEGACY_SHOPIFY_COOKIES.forEach((name) => {
      document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
    });
  }
  if (
    !readShopifyConsent()?.analyticsAllowed ||
    !hasShopifyTrackingValues(values)
  )
    return null;
  return values;
}

export async function refreshShopifyTracking({ syncConsent = false } = {}) {
  const consent = readShopifyConsent();
  if (!consent || (!consent.analyticsAllowed && !syncConsent)) return null;
  const consentKey = JSON.stringify(consent);
  if (trackingRequest && trackingConsent !== consentKey) {
    // Apply changed privacy choices after older responses, so an in-flight
    // acceptance cannot overwrite Shopify's cookie-expiry instructions.
    try {
      await trackingRequest;
    } catch {
      // A failed older request must not prevent applying the current choice.
    }
    return refreshShopifyTracking({ syncConsent });
  }
  // Share an in-flight initialization: simultaneous page/cart events must not
  // create two identities before either response has set the server cookies.
  if (!trackingRequest) {
    trackingConsent = consentKey;
    const request = async (attempt = 0) => {
      try {
        return await requestShopifyTracking(syncConsent);
      } catch (error) {
        if (error.retryable === false || attempt === 2) throw error;
        // Retry temporary failures only; each attempt rereads current consent.
        await new Promise((resolve) => {
          setTimeout(resolve, (attempt + 1) * 1000);
        });
        return request(attempt + 1);
      }
    };
    trackingRequest = (
      navigator.locks?.request
        ? navigator.locks.request('shopify-tracking', () => request())
        : request()
    ).finally(() => {
      trackingRequest = null;
      trackingConsent = null;
    });
  }
  const values = await trackingRequest;
  // A marketing/preferences change during the request also invalidates _cmp.
  // Wait for current Shopify values before sending the event's consent data.
  if (JSON.stringify(readShopifyConsent()) !== consentKey)
    return refreshShopifyTracking({ syncConsent });
  return values;
}
